import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Card, Popconfirm, Space, Table, Tooltip, Typography, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, DownloadOutlined, EyeOutlined, UploadOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  documentService,
  type Document as ApiDocument,
} from '@/services/document.service';
import {
  BOQ_LINE_ALLOWED_FILE_EXTENSIONS,
  BOQ_LINE_DOCUMENT_MAX_BYTES,
  BOQ_LINE_DOCUMENT_MAX_COUNT,
  DOCUMENT_ENTITY_TYPE_BOQ_LINE_ITEM,
} from '@/constants/document-entity-types';

const { Text } = Typography;

export interface EntityAttachmentsCardProps {
  entityType: string;
  /** When null/undefined, uploads and API calls are disabled. */
  entityId: string | null | undefined;
  title?: string;
  /** Hide upload and delete (read-only list). */
  readOnly?: boolean;
  queryKeyPrefix?: string[];
  /** Shown when `entityId` is missing (e.g. unsaved form). */
  saveFirstMessage?: string;
  /** Shown above the upload button when uploads are enabled. */
  uploadHint?: string;
  /** Called after a successful upload or delete. */
  onDocumentsChanged?: () => void;
  extra?: React.ReactNode;
  /** Stored on `documents.document_category`. Defaults to Technical so existing callers stay unchanged. */
  documentCategory?: string;
  /** Show only rows with this category (case-insensitive). */
  categoryEquals?: string;
  /** Hide rows with this category (case-insensitive). */
  categoryExcludes?: string;
  emptyText?: string;
  /** Invalidate every query that starts with this key (both attachment tabs on one line). */
  sharedInvalidateKey?: unknown[];
}

function extAllowed(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return BOQ_LINE_ALLOWED_FILE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export const EntityAttachmentsCard: React.FC<EntityAttachmentsCardProps> = ({
  entityType,
  entityId,
  title = 'Attachments',
  readOnly = false,
  queryKeyPrefix = ['documents', 'entity'],
  saveFirstMessage = 'Save the record first to add attachments.',
  uploadHint,
  onDocumentsChanged,
  extra,
  documentCategory = 'Technical',
  categoryEquals,
  categoryExcludes,
  emptyText = 'No attachments',
  sharedInvalidateKey,
}) => {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const enabled = Boolean(entityId?.trim());

  const queryKey = useMemo(
    () => [...queryKeyPrefix, entityType, entityId ?? ''],
    [queryKeyPrefix, entityType, entityId]
  );

  const { data: docs = [], isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!entityId?.trim()) return [];
      const list = await documentService.getDocuments({
        entity_type: entityType,
        entity_id: entityId,
      });
      return Array.isArray(list) ? list : [];
    },
    enabled,
  });

  const visibleDocs = useMemo(() => {
    return docs.filter((doc) => {
      const cat = String(doc.document_category || '').trim().toLowerCase();
      if (categoryEquals) return cat === categoryEquals.trim().toLowerCase();
      if (categoryExcludes) return cat !== categoryExcludes.trim().toLowerCase();
      return true;
    });
  }, [docs, categoryEquals, categoryExcludes]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
    if (sharedInvalidateKey && sharedInvalidateKey.length > 0) {
      queryClient.invalidateQueries({ queryKey: sharedInvalidateKey });
    }
  }, [queryClient, queryKey, sharedInvalidateKey]);

  const handleDownload = async (doc: ApiDocument) => {
    try {
      const blob = await documentService.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || doc.document_name || 'download';
      a.click();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch {
      message.error('Download failed');
    }
  };

  const handleView = async (doc: ApiDocument) => {
    try {
      await documentService.openDocumentInNewTab(doc.id);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e ? String((e as { message: string }).message) : 'View failed';
      message.error(msg);
    }
  };

  const handleDelete = async (docId: string) => {
    try {
      await documentService.deleteDocument(docId);
      message.success('Attachment removed');
      await refetch();
      invalidate();
      onDocumentsChanged?.();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e ? String((e as { message: string }).message) : 'Delete failed';
      message.error(msg);
    }
  };

  const beforeUpload = async (file: File) => {
    if (!entityId?.trim()) {
      message.warning(saveFirstMessage);
      return Upload.LIST_IGNORE;
    }
    if (!extAllowed(file.name)) {
      message.error(
        `Allowed types: ${BOQ_LINE_ALLOWED_FILE_EXTENSIONS.join(', ')}`
      );
      return Upload.LIST_IGNORE;
    }
    if (file.size > BOQ_LINE_DOCUMENT_MAX_BYTES) {
      message.error('File exceeds 25MB limit for BOQ line attachments');
      return Upload.LIST_IGNORE;
    }
    if (docs.length >= BOQ_LINE_DOCUMENT_MAX_COUNT) {
      message.error(`Maximum ${BOQ_LINE_DOCUMENT_MAX_COUNT} attachments per line`);
      return Upload.LIST_IGNORE;
    }
    setUploading(true);
    try {
      // document_type is varchar(50) — never send MIME (Office MIMEs are 65–73 chars).
      const ext = file.name.includes('.')
        ? file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase()
        : '';
      await documentService.uploadDocument(file, {
        entity_type: entityType,
        entity_id: entityId,
        document_type: ext || 'File',
        document_category: documentCategory,
      });
      message.success('Uploaded');
      await refetch();
      invalidate();
      onDocumentsChanged?.();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax.response?.data?.message || ax.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
    return false;
  };

  const columns: ColumnsType<ApiDocument> = [
    {
      title: 'File',
      dataIndex: 'document_name',
      ellipsis: true,
      render: (_: unknown, r) => r.file_name || r.document_name,
    },
    {
      title: 'Size',
      width: 90,
      render: (_: unknown, r) => documentService.formatFileSize(Number(r.file_size_bytes || 0)),
    },
    {
      title: 'Type',
      width: 100,
      ellipsis: true,
      render: (_: unknown, r) => r.mime_type || r.file_type || '—',
    },
    {
      title: 'Uploaded',
      width: 150,
      render: (_: unknown, r) => (r.created_at ? String(r.created_at).slice(0, 19).replace('T', ' ') : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: readOnly ? 88 : 120,
      align: 'right',
      fixed: 'right',
      render: (_: unknown, r) => (
        <Space size={0} wrap={false}>
          <Tooltip title="View">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => void handleView(r)} />
          </Tooltip>
          <Tooltip title="Download">
            <Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => void handleDownload(r)} />
          </Tooltip>
          {!readOnly ? (
            <Popconfirm title="Remove this attachment?" onConfirm={() => handleDelete(r.id)}>
              <Tooltip title="Delete">
                <Button type="link" size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  const showBoqHints = entityType === DOCUMENT_ENTITY_TYPE_BOQ_LINE_ITEM;
  const hintText =
    uploadHint ??
    (showBoqHints
      ? `PDF, images, or Office files, up to 25MB each; max ${BOQ_LINE_DOCUMENT_MAX_COUNT} files per line.`
      : null);

  return (
    <Card size="small" title={title} extra={extra} styles={{ body: { overflow: 'hidden' } }}>
      {!enabled ? (
        <Alert type="info" showIcon message={saveFirstMessage} />
      ) : (
        <>
          {isError ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 8 }}
              message={
                (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data
                  ?.message ||
                (error as { message?: string })?.message ||
                'Failed to load attachments'
              }
            />
          ) : null}
          {hintText ? (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              {hintText}
            </Text>
          ) : null}
          {!readOnly ? (
            <Upload accept={BOQ_LINE_ALLOWED_FILE_EXTENSIONS.join(',')} showUploadList={false} beforeUpload={beforeUpload}>
              <Button icon={<UploadOutlined />} loading={uploading} disabled={!enabled || docs.length >= BOQ_LINE_DOCUMENT_MAX_COUNT}>
                Upload
              </Button>
            </Upload>
          ) : null}
          <Table<ApiDocument>
            style={{ marginTop: 12 }}
            size="small"
            rowKey="id"
            loading={isLoading}
            dataSource={visibleDocs}
            pagination={false}
            columns={columns}
            scroll={{ x: 640 }}
            locale={{ emptyText }}
          />
        </>
      )}
    </Card>
  );
};

export default EntityAttachmentsCard;
